/**
 * GLSL code for baking textures for rendering ahead of time.
 * @module
 */
import * as THREE from "three";

export const bakeMaterialVertShader = `
varying vec2 vUv;

// <define_entry_point>

void main() {
  vec4 worldPos = modelMatrix * vec4( position, 1.0 );
  // <world_pos_entry_point>
  vUv = worldPos.xy;
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const bakeNormalMapDefinitions = `
uniform sampler2D normalMap;
uniform float normalScale;
uniform bool normalMapEnabled;
uniform mat3 normalMapUvTransform;
`;

const bakeNormalMap = `
vec3 mapN = vec3(0, 0, 1);
if (normalMapEnabled) {
  mapN = texture2D(normalMap, (normalMapUvTransform * vec3(vUv, 1)).xy).xyz * 2.0 - 1.0;
  mapN.xy *= normalScale;
  float mapNxyLength = length(mapN.xy);
  if (mapNxyLength > 0.01) {
    // Note that the normalMapUvTransform values are transposed here.
    mapN.xy = mapNxyLength * normalize(mat2(normalMapUvTransform[0][0], normalMapUvTransform[1][0], normalMapUvTransform[0][1], normalMapUvTransform[1][1]) * mapN.xy);
  }
  mapN.z += 1.0 - abs(normalScale);
}
vec3 packedNormal = packNormalToRGB(mapN);
`;

const bakeTextureChannelDefinitions = `
uniform sampler2D tex;
uniform vec2 texInvSize;
uniform vec4 channelMask;
`;

const bakeTextureChannel = `
float texChannel = dot(texture2D(tex, gl_FragCoord.xy * texInvSize), channelMask);
`;

export enum Bakeable {
  ZERO,
  NORMAL_MAP,
  DEPTH,
  TEXTURE_CHANNEL,
}

interface BakeableInfo {
  definitions: string;
  computeChunk: string;
  channelChunk: string;
  channelCount: number;
  uniforms: { [uniform: string]: THREE.IUniform };
}

function getBakeableInfo(bakeable: Bakeable): BakeableInfo {
  if (bakeable === Bakeable.ZERO) {
    // The precision of normal gl_FragCoord.z is enough here. See three.js depth shader for a higher precision alternative.
    return {
      definitions: "",
      computeChunk: "",
      channelChunk: "0",
      channelCount: 1,
      uniforms: {},
    };
  } else if (bakeable === Bakeable.NORMAL_MAP) {
    return {
      definitions: bakeNormalMapDefinitions,
      computeChunk: bakeNormalMap,
      channelChunk: "packedNormal",
      channelCount: 3,
      uniforms: {
        normalMapUvTransform: { value: new THREE.Matrix3() },
        normalMapEnabled: { value: false },
        normalScale: { value: 1.0 },
        normalMap: { value: null },
      },
    };
  } else if (bakeable === Bakeable.DEPTH) {
    // The precision of normal gl_FragCoord.z is enough here. See three.js depth shader for a higher precision alternative.
    return {
      definitions: "",
      computeChunk: "",
      channelChunk: "1.0 - gl_FragCoord.z",
      channelCount: 1,
      uniforms: {},
    };
  } else {
    if (!(bakeable === Bakeable.TEXTURE_CHANNEL)) {
      console.assert(false);
    }
    return {
      definitions: bakeTextureChannelDefinitions,
      computeChunk: bakeTextureChannel,
      channelChunk: "texChannel",
      channelCount: 1,
      uniforms: {
        tex: { value: null },
        texInvSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },
        channelMask: { value: new THREE.Vector4(0, 0, 0, 0) },
      },
    };
  }
}

export function getBakeMaterialFragShader(channels: Bakeable[]): string {
  let definitions = "";
  let compute = "";
  const channelsCode: string[] = [];
  let channelCount = 0;
  for (const channel of channels) {
    const packable = getBakeableInfo(channel);
    definitions += packable.definitions;
    compute += packable.computeChunk;
    channelsCode.push(packable.channelChunk);
    channelCount += packable.channelCount;
    if (!(channelCount <= 4)) {
      console.assert(false);
    }
  }
  while (channelCount < 4) {
    channelsCode.push("0");
    ++channelCount;
  }
  return `
#include <packing>

${definitions}

varying vec2 vUv;

void main() {
    ${compute}
  gl_FragColor = vec4(${channelsCode.join(",")});
}
`;
}

export function getBakeMaterialUniforms(channels: Bakeable[]): { [uniform: string]: THREE.IUniform } {
  let uniforms = {};
  for (const channel of channels) {
    const packable = getBakeableInfo(channel);
    uniforms = { ...uniforms, ...packable.uniforms };
  }
  return uniforms;
}
